// The Settings tab's sub-tabs, and the URL-fragment contract they share with
// the page that hosts them.
//
// The page owns the first fragment segment (`#settings`); this pane owns the
// second (`#settings/feedback_collection`). Splitting one hash between two tab
// systems — rather than letting both write bare keys into it — is what keeps
// the inner sub-nav from clobbering the outer tab (the BUGS-4407 failure mode
// InnerTabs' controlled mode exists for).
// Every sub-tab that ships has a member here, in design order.
export enum SettingsTabs {
  FEEDBACK_COLLECTION = 'feedback_collection',
  REPLY_ADDRESS = 'reply_address',
  GUEST_EXPERIENCE = 'guest_experience',
  NOTIFICATIONS = 'notifications',
}

// Declaration order IS the rendered sub-nav order, and the first entry is the
// default. Kept as an explicit array (not Object.values) so the order is a
// stated contract rather than a property of how the enum happens to be written.
export const SETTINGS_TAB_ORDER: SettingsTabs[] = [
  SettingsTabs.FEEDBACK_COLLECTION,
  SettingsTabs.REPLY_ADDRESS,
  SettingsTabs.GUEST_EXPERIENCE,
  SettingsTabs.NOTIFICATIONS,
]

export const DEFAULT_SETTINGS_TAB = SETTINGS_TAB_ORDER[0]

export const isSettingsTab = (value: string): value is SettingsTabs =>
  (SETTINGS_TAB_ORDER as string[]).includes(value)

// Reads the sub-tab out of `#<page-tab>/<sub-tab>`. Prefix-agnostic on purpose:
// the host passes its own tab key in as `hashPrefix`, so this never has to
// duplicate the page's `Tabs.SETTINGS` literal. An absent or unknown second
// segment returns null and the caller falls back to DEFAULT_SETTINGS_TAB.
export const readSettingsTabFromHash = (hash: string): SettingsTabs | null => {
  const value = hash.replace('#', '').split('/')[1] ?? ''
  return isSettingsTab(value) ? value : null
}

// Builds the fragment for a sub-tab under the given page tab, e.g.
// ('settings', 'feedback_collection') -> 'settings/feedback_collection'.
// Returned without a leading '#' because history.replace({ hash }) adds one.
export const buildSettingsHash = (
  hashPrefix: string,
  tab: SettingsTabs
): string => `${hashPrefix}/${tab}`
